import React, { useState } from 'react';
import { TrendingUp, Globe } from 'lucide-react';
import { PaperTradingPage } from './PaperTradingPage';
import { ForexPanel } from './ForexPanel';

type AssetTab = 'equity' | 'forex';

const TAB_STATE_KEY = 'paperTrading.activeAssetTab';

/**
 * Paper-trading shell — owns the asset-class tab selector (Equity | Forex)
 * and renders the appropriate panel below.
 *
 * Equity panel = the existing 1.2K-line PaperTradingPage component
 * (Alpaca, scanner-driven automation, full trade journal). Untouched
 * by this refactor — wrapped, not rewritten.
 *
 * Forex panel = the new FXCM-shaped ForexPanel (manual orders today,
 * automation rules in Phase D).
 *
 * Active tab persists to localStorage so a user who lives mostly in
 * forex doesn't have to re-pick it on every page load.
 */
export function PaperTradingShell() {
  const [tab, setTab] = useState<AssetTab>(() => {
    if (typeof window === 'undefined') return 'equity';
    const stored = window.localStorage.getItem(TAB_STATE_KEY);
    return stored === 'forex' ? 'forex' : 'equity';
  });

  const switchTo = (next: AssetTab) => {
    setTab(next);
    try { window.localStorage.setItem(TAB_STATE_KEY, next); } catch { /* private mode — ignore */ }
  };

  return (
    <div className="flex flex-col gap-3 h-full">
      {/* ── Tab strip ────────────────────────────────────────────── */}
      <div className="flex items-center gap-1 border-b">
        <TabButton
          icon={<TrendingUp className="h-4 w-4" />}
          label="Equity"
          subtitle="Alpaca · stocks + ETFs"
          active={tab === 'equity'}
          onClick={() => switchTo('equity')}
        />
        <TabButton
          icon={<Globe className="h-4 w-4" />}
          label="Forex"
          subtitle="FXCM · currency pairs"
          active={tab === 'forex'}
          onClick={() => switchTo('forex')}
        />
      </div>

      {/* ── Panel body ───────────────────────────────────────────── */}
      <div className="flex-1 min-h-0 overflow-auto">
        {tab === 'equity' && <PaperTradingPage />}
        {tab === 'forex' && <ForexPanel />}
      </div>
    </div>
  );
}

function TabButton({
  icon, label, subtitle, active, onClick,
}: {
  icon: React.ReactNode;
  label: string;
  subtitle: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={[
        'flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors',
        active
          ? 'border-primary text-foreground'
          : 'border-transparent text-muted-foreground hover:text-foreground',
      ].join(' ')}
      aria-selected={active}
      role="tab"
    >
      {icon}
      <div className="flex flex-col items-start leading-tight">
        <span>{label}</span>
        <span className="text-[10px] font-normal text-muted-foreground">{subtitle}</span>
      </div>
    </button>
  );
}
